import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { StationDemandForecast } from "@app/core/models/demandsForecast";
import { ChartModule } from "primeng/chart";

@Component({
  selector: "app-demands-forecast-graph",
  templateUrl: "./demands-forecast-graph.component.html",
  styleUrl: "./demands-forecast-graph.component.scss",
  imports: [ChartModule],
  standalone: true,
})
export class DemandsForecastGraphComponent implements OnChanges {
  @Input() demand!: StationDemandForecast;
  @Input() currentBikeCount!: number;
  @Input() setPoint!: number;

  arrivalsAndDepartures: any;
  dataDiference: any;
  dataAktual: any;

  options: any = {
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: "Čas (hodiny)",
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: "Počet kol",
        },
        grid: {
          display: true,
          color: (context: any) => {
            if (context.tick.value === 0) {
              return "#000";
            }
            return "#cecece";
          },
        },
      },
    },
  };

  ngOnChanges(changes: SimpleChanges) {
    setTimeout(() => {
      this.updateChartData();
    }, 0);
  }

  private updateChartData() {
    const documentStyle = getComputedStyle(document.documentElement);

    const historyLength = this.demand.historyArrivals?.length || 0;
    const forecastLength = this.demand.arrivals?.length || 0;
    const totalPoints = historyLength + forecastLength;

    // agregate settings
    let chunkSize = 1;
    let labelFormat: "time" | "dayTime" = "time";

    if (totalPoints > 96 && totalPoints <= 192) {
      chunkSize = 4;
      labelFormat = "dayTime";
    } else if (totalPoints > 192 && totalPoints <= 288) {
      chunkSize = 4;
      labelFormat = "dayTime";
    } else if (totalPoints > 288) {
      chunkSize = 16;
      labelFormat = "dayTime";
    }

    // --- agregate dat ---
    const historyArrivals = this.aggregateData(this.demand.historyArrivals || [], chunkSize);
    const historyDepartures = this.aggregateData(this.demand.historyDepartures || [], chunkSize);
    
    const forecastArrivals = this.aggregateData(this.demand.arrivals || [], chunkSize);
    const forecastDepartures = this.aggregateData(this.demand.departures || [], chunkSize);

    const allArrivals = [...historyArrivals, ...forecastArrivals];
    const allDepartures = [...historyDepartures, ...forecastDepartures];

    const labels = this.generateLabels(historyLength, forecastLength, chunkSize, labelFormat);
    
    // index where history ends
    const historyEndIndex = Math.ceil(historyLength / chunkSize);

    const difference = allArrivals.map((arrivals, i) => arrivals - allDepartures[i]);

    const cumulativeState = this.calculateCumulativeState(
      difference,
      this.currentBikeCount || 0,
      historyEndIndex
    );

    const setPointLine = new Array(labels.length).fill(this.setPoint);

    // separate history and forecast data for diference and cumulative state
    const historyDifferenceData = difference.slice(0, historyEndIndex);
    const forecastDifferenceData = difference.slice(historyEndIndex);

    const historyCumulativeData = cumulativeState.slice(0, historyEndIndex);
    const forecastCumulativeData = [
      cumulativeState[historyEndIndex - 1] || this.currentBikeCount,
      ...cumulativeState.slice(historyEndIndex)
    ];

    // --- chart data current state---
    this.dataAktual = {
      labels: labels,
      datasets: [
        {
          type: "line",
          label: "Set Point",
          borderColor: documentStyle.getPropertyValue("--orange-500"),
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          tension: 0,
          pointRadius: 0,
          data: setPointLine,
        },
        {
          type: "bar",
          label: "Historie - Diference",
          backgroundColor: documentStyle.getPropertyValue("--gray-500"),
          data: this.createDataWithGaps(historyDifferenceData, 0, historyEndIndex, labels.length),
          borderColor: "white",
          borderWidth: 1,
        },
        {
          type: "bar",
          label: "Předpověď - Diference",
          backgroundColor: documentStyle.getPropertyValue("--green-500"),
          data: this.createDataWithGaps(forecastDifferenceData, historyEndIndex, labels.length, labels.length),
          borderColor: "white",
          borderWidth: 1,
        },
        {
          type: "line",
          label: "Historie - Stav",
          borderColor: documentStyle.getPropertyValue("--gray-500"),
          borderWidth: 2,
          fill: false,
          tension: 0,
          data: this.createDataWithGaps(historyCumulativeData, 0, historyEndIndex, labels.length),
        },
        {
          type: "line",
          label: "Předpověď - Stav",
          borderColor: documentStyle.getPropertyValue("--blue-500"),
          borderWidth: 3,
          fill: false,
          tension: 0,
          data: this.createDataWithGaps(forecastCumulativeData, historyEndIndex - 1, labels.length, labels.length),
        },
      ],
    };

    // --- chart data arrivals and departures ---
    const historyArrivalsData = this.createDataWithGaps(historyArrivals, 0, historyEndIndex, labels.length);
    const historyDeparturesData = this.createDataWithGaps(historyDepartures, 0, historyEndIndex, labels.length);
    const forecastArrivalsData = this.createDataWithGaps(
      [historyArrivals[historyArrivals.length - 1] || 0, ...forecastArrivals],
      historyEndIndex - 1,
      labels.length,
      labels.length
    );
    const forecastDeparturesData = this.createDataWithGaps(
      [historyDepartures[historyDepartures.length - 1] || 0, ...forecastDepartures],
      historyEndIndex - 1,
      labels.length,
      labels.length
    );

    this.arrivalsAndDepartures = {
      labels: labels,
      datasets: [
        {
          type: "line",
          label: "Historie - Příjezdy",
          backgroundColor: documentStyle.getPropertyValue("--blue-200"),
          borderColor: documentStyle.getPropertyValue("--blue-200"),
          data: historyArrivalsData,
          fill: false,
        },
        {
          type: "line",
          label: "Historie - Odjezdy",
          backgroundColor: documentStyle.getPropertyValue("--red-200"),
          borderColor: documentStyle.getPropertyValue("--red-200"),
          data: historyDeparturesData,
          fill: false,
        },
        {
          type: "line",
          label: "Předpověď - Příjezdy",
          backgroundColor: documentStyle.getPropertyValue("--blue-500"),
          borderColor: documentStyle.getPropertyValue("--blue-500"),
          data: forecastArrivalsData,
          fill: false,
          borderWidth: 2,
        },
        {
          type: "line",
          label: "Předpověď - Odjezdy",
          backgroundColor: documentStyle.getPropertyValue("--red-500"),
          borderColor: documentStyle.getPropertyValue("--red-500"),
          data: forecastDeparturesData,
          fill: false,
          borderWidth: 2,
        },
      ],
    };

    this.dataDiference = {
      labels: labels,
      datasets: [
        {
          label: "Diference",
          backgroundColor: documentStyle.getPropertyValue("--green-500"),
          borderColor: documentStyle.getPropertyValue("--green-500"),
          data: difference,
        },
      ],
    };
  }

  /**
   * Creates an array of data with gaps (null) outside the specified range
   */
  private createDataWithGaps(
    data: number[],
    startIdx: number,
    endIdx: number,
    totalLength: number
  ): (number | null)[] {
    const result: (number | null)[] = new Array(totalLength).fill(null);
    for (let i = 0; i < data.length && (startIdx + i) < endIdx; i++) {
      result[startIdx + i] = data[i];
    }
    return result;
  }

  /**
   * Calculates the cumulative state from the difference
   * For history, it calculates backward, for forecast forward
   */
  private calculateCumulativeState(
    difference: number[],
    currentBikeCount: number,
    historyEndIndex: number
  ): number[] {
    const state: number[] = new Array(difference.length);

    state[historyEndIndex - 1] = currentBikeCount;

    // Backward calculation for history (from now to past)
    for (let i = historyEndIndex - 2; i >= 0; i--) {
      state[i] = state[i + 1] - difference[i + 1];
    }

    // Forward calculation for forecast (from now to future)
    for (let i = historyEndIndex; i < difference.length; i++) {
      state[i] = state[i - 1] + difference[i];
    }

    return state;
  }

  private aggregateData(data: number[], chunkSize: number): number[] {
    if (chunkSize <= 1 || !data || data.length === 0) {
      return data || [];
    }
    const aggregated = [];
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      const sum = chunk.reduce((acc, val) => acc + val, 0);
      aggregated.push(sum);
    }
    return aggregated;
  }

  /**
   * Generates labels for the X axis
   * History goes backward from "now", forecast forward
   */
  private generateLabels(
    historyLength: number,
    forecastLength: number,
    chunkSize: number,
    format: "time" | "dayTime"
  ): (string | string[])[] {
    const labels: (string | string[])[] = [];
    const now = new Date();
    const dayNames = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];

    // Round up to the nearest 15 minutes
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;
    const currentTime = new Date(now);
    currentTime.setMinutes(roundedMinutes, 0, 0);

    const totalPoints = historyLength + forecastLength;

    for (let i = 0; i < totalPoints; i += chunkSize) {
      // Offset from the current time
      // For history: negative offset (i - historyLength)
      // For forecast: positive offset (i - historyLength)
      const offsetMinutes = (i - historyLength) * 15;
      const pointDate = new Date(currentTime.getTime() + offsetMinutes * 60 * 1000);

      const hours = pointDate.getHours().toString().padStart(2, "0");
      const minutes = pointDate.getMinutes().toString().padStart(2, "0");
      const timeString = `${hours}:${minutes}`;

      if (format === "dayTime") {
        const dayString = dayNames[pointDate.getDay()];
        labels.push([dayString, timeString]);
      } else {
        labels.push(timeString);
      }
    }
    return labels;
  }
}